import {Box, HStack} from '@chakra-ui/layout';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {routes, useLivraisonsPossiblesContext} from 'ff-common';
import {NewLivraisonButtonMobile} from '../components/NewLivraisonButtonMobile';
import {InfoLivraison} from '../components/InfoLivraison';
import {useHalState, useHalStates} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {
  LivraisonDesc,
  MarketplaceDesc,
  PanierDesc,
} from 'api-types/ffconnect-hal';
import {CatalogueMarketplace} from 'api-types/ffconnect';
import {getModeFromUrl} from '@/utils/appMode';
import {HeaderPanierListe} from './HeaderPanierListe';
import {useClient, useLivraisons} from '@/haligator-factory';
import {useTotalPaniers} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {CheckLoaded} from '@/containers/CheckLoaded';

interface PanierListeProps {
  isMarketplace?: boolean;
  currentPanierId?: string;
}

export const PanierListe: React.FC<PanierListeProps> = ({
  isMarketplace,
  currentPanierId,
}) => {
  const client = useClient();
  const totalPaniers = useTotalPaniers(client);
  return (
    <>
      <HeaderPanierListe nbPaniers={totalPaniers} />
      <PanierListeUnifiedContent
        isMarketplace={isMarketplace}
        currentPanierId={currentPanierId}
      />
    </>
  );
};

const PanierListeUnifiedContent: React.FC<{
  isMarketplace?: boolean;
  currentPanierId?: string;
}> = ({isMarketplace, currentPanierId}) => {
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();
  const livraisonsPossibles = useHalStates(useLivraisons().followAll('items'));
  const client = useClient();
  const marketplacesHook = useHalStates(
    client.follow('marketplaces').followAll('items'),
  );

  const livraisonsAvecPanier =
    livraisonsPossibles.states?.filter((livraison) =>
      livraison.hasOneLink('panier_ouvert'),
    ) ?? [];
  const marketplaces = marketplacesHook.states ?? [];

  if (livraisonsAvecPanier.length === 0 && marketplaces.length === 0) {
    return null;
  }

  const marketplaceChips = marketplaces.map((marketplaceState) => (
    <MarketplaceCartChip
      key={marketplaceState.data.marketplace_id}
      marketplaceState={marketplaceState}
      currentPanierId={currentPanierId}
    />
  ));
  const regularChips = livraisonsAvecPanier.map((livraisonState) => (
    <PanierItemLivraison
      key={livraisonState.data.id}
      livraisonState={livraisonState}
      isActive={
        !isMarketplace &&
        livraisonState.resource.uri === livraisonSelectionnee.uri
      }
    />
  ));

  return (
    <Box
      p='2'
      pb='3'
      w='100%'
    >
      <Box
        overflowX='auto'
        width='100%'
      >
        <HStack w='max-content'>
          {isMarketplace ? (
            <>
              {marketplaceChips}
              {regularChips}
            </>
          ) : (
            <>
              {regularChips}
              {marketplaceChips}
            </>
          )}
        </HStack>
        {livraisonsAvecPanier.length > 0 && (
          <Box display={{base: 'block', lg: 'none'}}>
            <NewLivraisonButtonMobile />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const MarketplaceCartChip: React.FC<{
  marketplaceState: State<MarketplaceDesc>;
  currentPanierId?: string;
}> = ({marketplaceState, currentPanierId}) => {
  const client = useClient();
  const mode = getModeFromUrl();
  const marketplaceId = marketplaceState.data.marketplace_id;
  const rempli_par = mode === 'commercial' ? 'representant' : 'client';
  const paniersHook = useHalStates(
    client
      .follow('paniers', {
        type_panier: marketplaceId,
        statut: 'ouvert',
        rempli_par,
      })
      .followAll('items'),
  );
  const panierState = paniersHook.states?.[0];

  if (!panierState || paniersHook.error) {
    return null;
  }

  const isActive = panierState.data.id === currentPanierId;
  const marketplaceName = marketplaceState.data.nom;
  const logoSrc = marketplaceState.data.logo_horizontal_couleur
    ? `${import.meta.env['VITE_MEDIA_URL']}${marketplaceState.data.logo_horizontal_couleur}`
    : undefined;

  return (
    <PanierItemMarketplace
      panierState={panierState}
      marketplaceName={marketplaceName}
      logoSrc={logoSrc}
      isActive={isActive}
      marketplaceId={marketplaceId}
    />
  );
};

const PanierItemLivraison: React.FC<{
  livraisonState: State<LivraisonDesc>;
  isActive: boolean;
}> = ({livraisonState, isActive}) => {
  const {setLivraisonSelectionnee} = useLivraisonsPossiblesContext();
  const {buildClientRoute} = useClientRoute();
  const navigate = useNavigate();

  const panierHook = useHalState(livraisonState.follow('panier_ouvert'));
  const {date_de_livraison} = livraisonState.data.tolede;

  const onClick = () => {
    setLivraisonSelectionnee(livraisonState);
    navigate(
      buildClientRoute(routes.client.panier, {
        date_panier_actif: date_de_livraison,
      }),
    );
  };

  return (
    <CheckLoaded
      hook={panierHook}
      render={({state, data}) => (
        <InfoLivraison
          onClick={onClick}
          date_de_livraison={date_de_livraison}
          nb_article={data.nombre_de_lignes}
          active={isActive}
          // Le lien n'est servi que pour un panier issu d'un import PDF
          isPanierPdf={state.hasOneLink('processus')}
        />
      )}
    />
  );
};

const PanierItemMarketplace: React.FC<{
  panierState: State<PanierDesc>;
  marketplaceName?: string;
  logoSrc?: string;
  isActive: boolean;
  marketplaceId: CatalogueMarketplace;
}> = ({panierState, marketplaceName, logoSrc, isActive, marketplaceId}) => {
  const {t} = useTranslation('common');
  const {setSelectedMarketplaceId} = useLivraisonsPossiblesContext();
  const {buildClientRoute} = useClientRoute();
  const navigate = useNavigate();

  const targetPath = buildClientRoute(routes.client.panier_marketplace, {
    marketplace_id: marketplaceId,
  });

  return (
    <InfoLivraison
      onClick={() => {
        setSelectedMarketplaceId(marketplaceId);
        navigate(targetPath);
      }}
      date_de_livraison={marketplaceName ?? ''}
      deliveryLabel={t('panier.livraison_48h_72h')}
      logoSrc={logoSrc}
      nb_article={panierState.data.nombre_de_lignes}
      active={isActive}
    />
  );
};
